import React from 'react';

import PropTypes from 'prop-types';

import {BarGauge} from '../bar-gauge';
import {FuelGauge} from '../fuel-gauge';
import {
    calculateCurrentPercentToEndTimeFromJson,
    calculatePercentComplete,
    generateTooltipText,
    formatCurrency,
    hasQuota,
} from '../quota';

import styles from './quota-row.module.css';

const quotaObjectPropType = PropTypes.oneOfType([
    PropTypes.shape({
        amount: PropTypes.shape({
            currency: PropTypes.string.isRequired,
            formatted: PropTypes.string,
            value: PropTypes.number.isRequired,
            prefix: PropTypes.string,
            suffix: PropTypes.string,
        }).isRequired,
    }),
    PropTypes.shape({
        count: PropTypes.number.isRequired,
    }),
]);

QuotaRow.prototype.propTypes = {
    name: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['bar', 'fuel']),
    quotaType: PropTypes.oneOf(['sales', 'activities']),
    assignee: PropTypes.shape({
        name: PropTypes.string.isRequired,
    }).isRequired,
    currentValue: quotaObjectPropType.isRequired,
    quotaValue: quotaObjectPropType.isRequired,
    index: PropTypes.number.isRequired,

    /**
     * We'll leverage the loggedInUser object here to see if the viewing
     * user is an admin - which determines what kind of helpful setup
     * links we can give them.
     */
    loggedInUser: PropTypes.object,
};

export function QuotaRow(props) {
    const {index, type, loggedInUser, ...modelJson} = props;
    const hasNonzeroQuota = hasQuota(modelJson);

    return (
        <div
            className={
                type === 'bar' ? styles['quota-container'] : styles['quota-container--full-height']
            }
        >
            <div className={styles['header-container']}>
                {renderTitle(modelJson.assignee.name, modelJson.name)}
                {hasNonzeroQuota
                    ? renderStatus(
                          modelJson.currentValue,
                          modelJson.quotaValue,
                          modelJson.quotaType
                      )
                    : renderNoQuotaMessage(type, modelJson.quotaType, loggedInUser)}
            </div>
            {renderGauge(type, modelJson, index)}
            {type === 'fuel' ? renderDetails(modelJson, loggedInUser) : undefined}
        </div>
    );
}

function renderTitle(assigneeName, quotaName) {
    return (
        <div className={styles['title-container']}>
            <span>{quotaName}</span>
            <span className={styles.assignee}>{assigneeName}</span>
        </div>
    );
}

function renderStatus(current, quota, quotaType) {
    let currentValue, quotaValue;
    if (quotaType === 'sales') {
        currentValue = renderCurrency(current.amount);
        quotaValue = renderCurrency(quota.amount);
    } else {
        currentValue = current.count;
        quotaValue = quota.count;
    }

    return (
        <div className={styles['status-container']}>
            <div className={styles['current-value']}>{currentValue}</div>
            <span className={styles['status-divider']}>/</span>
            <div className={styles['quota-value']}>{quotaValue}</div>
        </div>
    );
}

function renderNoQuotaMessage(style, quotaType, loggedInUser) {
    if (style === 'bar') {
        return (
            <div className={styles['status-container--no-quota']}>
                {renderQuotaSetupLink(quotaType, 'Set a quota', loggedInUser)}
            </div>
        );
    }
}

function renderGauge(style, modelJson, index) {
    const hasNonzeroQuota = hasQuota(modelJson);
    const shouldRenderTodayLabel = Boolean(index === 0);
    const percentComplete = calculatePercentComplete(modelJson);
    const currentPercentToEndTime = calculateCurrentPercentToEndTimeFromJson(modelJson);
    const tooltip = generateTooltipText(modelJson, currentPercentToEndTime);

    switch (style) {
        case 'bar':
            return (
                <BarGauge
                    hasQuota={hasNonzeroQuota}
                    percentComplete={percentComplete}
                    currentPercentToEndTime={currentPercentToEndTime}
                    statusTooltip={tooltip}
                    shouldRenderTodayLabel={shouldRenderTodayLabel}
                />
            );
        case 'fuel':
            return (
                <FuelGauge
                    hasQuota={hasNonzeroQuota}
                    percentComplete={percentComplete}
                    currentPercentToEndTime={currentPercentToEndTime}
                />
            );
        default:
            return undefined;
    }
}

function renderDetails(modelJson, loggedInUser) {
    const hasNonzeroQuota = hasQuota(modelJson);
    if (hasNonzeroQuota) {
        return undefined;
    }

    return renderQuotaSetupPrompt(modelJson.quotaType, loggedInUser);
}

function renderQuotaSetupLink(quotaType, label, loggedInUser) {
    const editLink = quotaType === 'sales' ? '/setup/quota' : '/setup/activity-quota';

    return loggedInUser && loggedInUser.permissions.canAccessSetup ? (
        <a href={editLink} className={styles['quota-edit-link']}>
            {label}
        </a>
    ) : undefined;
}

function renderQuotaSetupPrompt(quotaType, loggedInUser) {
    return (
        <div className={styles['setup-prompt-container']}>
            <span>No quota set</span>
            {renderQuotaSetupLink(quotaType, 'Edit this quota', loggedInUser)}
        </div>
    );
}

function renderCurrency(currency, currencyToSubtract) {
    const {value: currencyValue, suffix: currencySuffix} = formatCurrency(
        currency,
        currencyToSubtract
    );

    return (
        <div>
            <span className={styles['currency-prefix']}>{currency.prefix}</span>
            <span className={styles['currency-value']}>{currencyValue}</span>
            <span className={styles['currency-suffix']}>{currencySuffix}</span>
        </div>
    );
}
